import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { CA_PLACEHOLDER, DOG_IMG, TG_URL, X_URL } from "@/lib/site";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const CaBox = () => {
    const [copied, setCopied] = useState(false);
    const onCopy = () => {
        navigator.clipboard?.writeText(CA_PLACEHOLDER).catch(() => {});
        setCopied(true);
        toast.success("Contract address copied — welcome to the pumpkin pack!");
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div
            data-testid="hero-contract-address-box"
            className="glass-panel animate-glow-pulse flex w-full max-w-md items-center justify-between gap-3 rounded-full py-2 pl-5 pr-2"
        >
            <span className="font-mono2 truncate text-xs font-bold tracking-wider text-orange-300 sm:text-sm">
                CA · {CA_PLACEHOLDER}
            </span>
            <button
                onClick={onCopy}
                data-testid="copy-ca-btn"
                aria-label="Copy contract address"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500 text-[#0D0B14] transition-[box-shadow,transform] duration-300 hover:scale-105 hover:shadow-[0_0_18px_rgba(255,107,0,0.7)]"
            >
                {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
        </div>
    );
};

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const moonY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const dogY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden pt-24 pb-16">
            <motion.div style={{ y: moonY }} className="pointer-events-none absolute -top-24 right-[-10%] h-[34rem] w-[34rem] opacity-70" aria-hidden="true">
                <div className="animate-moon h-full w-full rounded-full bg-[radial-gradient(circle,rgba(255,140,0,0.28)_0%,rgba(157,0,255,0.12)_45%,transparent_70%)] blur-2xl" />
            </motion.div>

            <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 lg:grid-cols-[1.2fr_1fr]">
                <div>
                    <MaskedLine delay={0.15}>
                        <span className="font-mono2 text-xs font-bold uppercase tracking-[0.35em] text-emerald-400 text-glow-green">
                            Pupoweenwifhat · Solana Memecoin
                        </span>
                    </MaskedLine>
                    <h1 className="font-spooky mt-5 leading-[0.95]">
                        <MaskedLine delay={0.3}>
                            <span className="text-6xl text-orange-400 text-glow-orange sm:text-7xl lg:text-8xl">Small Dog</span>
                        </MaskedLine>
                        <MaskedLine delay={0.45}>
                            <span className="text-stroke-orange text-6xl sm:text-7xl lg:text-8xl">Big Dreams</span>
                        </MaskedLine>
                        <MaskedLine delay={0.6}>
                            <span className="text-5xl text-slate-100 sm:text-6xl lg:text-7xl">$POWEEN</span>
                        </MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.85 }}
                        className="mt-6 max-w-lg text-sm leading-relaxed text-slate-400 sm:text-base"
                    >
                        The spookiest, cutest, most unhinged pup on Solana. Fueled by pumpkin
                        spice, spectral gains, and moonbound doge magic. The hat stays on.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1 }}
                        className="mt-8 flex flex-col gap-4"
                    >
                        <CaBox />
                        <div className="flex flex-wrap gap-3">
                            <a
                                href={TG_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-telegram"
                                className="rounded-full bg-orange-500 px-7 py-3 font-mono2 text-sm font-bold uppercase tracking-wider text-[#0D0B14] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(255,107,0,0.7)]"
                            >
                                Join Telegram
                            </a>
                            <a
                                href={X_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-x"
                                className="rounded-full border border-emerald-400/40 bg-emerald-400/5 px-7 py-3 font-mono2 text-sm font-bold uppercase tracking-wider text-emerald-300 transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_0_30px_rgba(0,255,135,0.35)]"
                            >
                                Follow X
                            </a>
                        </div>
                    </motion.div>
                </div>

                <motion.div style={{ y: dogY }} className="relative mx-auto w-full max-w-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="animate-glow-pulse overflow-hidden rounded-t-[12rem] rounded-b-3xl border border-orange-500/30"
                    >
                        <img
                            src={DOG_IMG}
                            alt="Pupoweenwifhat — pup in a knitted pumpkin hat"
                            data-testid="hero-dog-image"
                            className="aspect-[4/5] w-full object-cover"
                        />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
